import React, { useState, useEffect, useCallback, useRef } from 'react';
import useOrders from '../hooks/useOrders';
import VehicleTrackingFilters from '../components/vehicle-tracking/VehicleTrackingFilters';
import VehicleTrackingTable from '../components/vehicle-tracking/VehicleTrackingTable';
import VehicleTrackingModal from '../components/vehicle-tracking/VehicleTrackingModal';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { useErrorDialog } from '../context/errorDialogContext';
import MessageAlert from '../components/common/MessageAlert';
import { useTranslation } from 'react-i18next';
import { todayIST } from '../utils/dates';

const PAGE_LIMIT = 20;

const VehicleTrackingPage = () => {
  const { t } = useTranslation();
  const {
    vehicleTracking,
    vehicleTrackingTotal,
    loading,
    error,
    fetchVehicleTracking,
    clearError
  } = useOrders();

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // UI States
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const { showError } = useErrorDialog();
  const searchDebounceRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  const loadData = useCallback(async () => {
    // Validation
    const now = todayIST();
    if (fromDate > now) {
      showError(t('common.messages.fromDateFuture'), t('common.messages.validationError'));
      return;
    }
    if (fromDate && toDate && fromDate > toDate) {
      showError(t('common.messages.fromDateGreaterThanTo'), t('common.messages.validationError'));
      return;
    }

    const skip = (currentPage - 1) * PAGE_LIMIT;
    const params = {
      skip,
      limit: PAGE_LIMIT,
    };

    if (committedSearch) params.vehicle_number = committedSearch;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (status) params.status = status;

    await fetchVehicleTracking(params);
  }, [currentPage, committedSearch, fromDate, toDate, status, fetchVehicleTracking, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (error) {
      showError(error, 'Vehicle Tracking Error');
      clearError();
    }
  }, [error, showError, clearError]);

  const handleRefresh = () => {
    loadData();
  };

  const handleReset = () => {
    setSearchTerm('');
    setCommittedSearch('');
    setFromDate('');
    setToDate('');
    setStatus('');
    setCurrentPage(1);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const totalPages = Math.ceil(vehicleTrackingTotal / PAGE_LIMIT);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {alertMessage && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert 
            type={alertMessage.type} 
            title={alertMessage.title} 
            message={alertMessage.message} 
            onClose={() => setAlertMessage(null)} 
          />
        </div>
      )}

      {/* Header Section - Matches OrdersPage */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            Vehicle Tracking
          </h1>
          <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            Monitor and track sub-orders assigned to vehicles across DO, DC, and Sales Daybook.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div
            className="flex items-center gap-3 rounded-2xl border px-4 py-3"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: '#fff' }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.15),
                color: UI_COLORS.BRAND.PRIMARY,
              }}
            >
              <FontAwesomeIcon icon={faTruck} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                Total Tracked
              </p>
              <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {vehicleTrackingTotal}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Uses ActionToolbar internally */}
      <VehicleTrackingFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        status={status}
        onStatusChange={(val) => { setStatus(val); setCurrentPage(1); }}
        onRefresh={handleRefresh}
        onReset={handleReset}
        refreshLoading={loading}
      />

      {/* Table Section - Matches OrdersPage Style */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden">
        <VehicleTrackingTable 
          items={vehicleTracking}
          loading={loading}
          onView={handleView}
        />
      </div>

      {/* Pagination - Matches OrdersPage Logic & Style */}
      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
          style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
        >
          <span className="text-sm" style={{ color: '#6b7280' }}>
            Page {currentPage} of {totalPages} — {vehicleTrackingTotal} total records
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                acc.push(p);
                return acc;
              }, [])
              .map((item) =>
                typeof item === 'string' ? (
                  <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    disabled={loading}
                    className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                      color: item === currentPage ? '#fff' : '#374151',
                      border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                    }}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: '#0066cc', color: '#fff' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <VehicleTrackingModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        data={selectedItem}
      />
    </div>
  );
};

export default VehicleTrackingPage;
